import { DownloadIcon } from '@heroicons/react/solid';

import { downloadAudio, generateAudioFilename } from '@/utils/downloadAudio';

import { SimpleTooltip } from '@/components/ui/tooltip';

interface Player {
  meta: {
    title: string;
    downloadUrl?: string;
    audio: {
      src: string;
      type: string;
    };
  };
}

interface DownloadButtonProps {
  player: Player;
}

// Helper function to extract file extension from URL
function getFileExtensionFromUrl(url: string): string {
  try {
    // Parse the URL to handle query parameters and fragments
    const parsedUrl = new URL(url);
    const pathname = parsedUrl.pathname;

    // Extract extension from pathname
    const lastDotIndex = pathname.lastIndexOf('.');
    if (lastDotIndex > 0 && lastDotIndex < pathname.length - 1) {
      return pathname.substring(lastDotIndex);
    }
  } catch (error) {
    // If URL parsing fails, try simple string method
    const lastDotIndex = url.lastIndexOf('.');
    const lastSlashIndex = url.lastIndexOf('/');

    if (lastDotIndex > lastSlashIndex && lastDotIndex < url.length - 1) {
      const extension = url.substring(lastDotIndex);
      // Only return if it looks like a valid audio extension
      if (/^\.(mp3|wav|m4a|aac|ogg|flac|wma)$/i.test(extension)) {
        return extension;
      }
    }
  }

  // Default fallback
  return '.mp3';
}

export function DownloadButton({ player }: DownloadButtonProps) {
  const handleDownload = async () => {
    if (!player.meta?.audio?.src) return;

    const fileExtension = getFileExtensionFromUrl(player.meta.audio.src);
    const filename = generateAudioFilename(
      player.meta.title || 'audio',
      fileExtension,
    );

    await downloadAudio({
      url: player.meta.downloadUrl || player.meta.audio.src,
      filename,
      onError: (error) => {
        console.error('Audio download failed:', error);
      },
    });
  };

  return (
    <SimpleTooltip title='Download audio'>
      <button
        type='button'
        className='group relative rounded-md hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 md:order-none'
        onClick={handleDownload}
        aria-label='Download audio'
      >
        <div className='absolute -inset-4 md:hidden' />
        <DownloadIcon className='h-5 w-5 fill-slate-500 stroke-slate-500 group-hover:fill-slate-700 group-hover:stroke-slate-700' />
      </button>
    </SimpleTooltip>
  );
}
